import { ComponentIds, Entity, HierarchyUtils, PlacementResult } from '@inter-ikea-core/core';
import {
  BestaFrameLegsFeatureSystem,
  FrameSelectedLegComponent
} from '@inter-ikea-storageone/range-besta';
import { setSupportLegChoice } from 'ducks/sheets';
import { BehaviorParameters } from 'rangeConfig/@types';
import { CommunicationSizeComponent } from '@inter-ikea-core/core';
import { CoreApi, ErrorMessages } from '@inter-ikea-storageone/range';
import {
  QuestionSheetState,
  setQuestionSheetVisible,
  setQuestionSheetVisibleWithProps
} from 'ducks/questionSheet';
import supportLegsImage from '../images/supportLegs.png';
import { hideNoFramesInSceneErrorMessage } from 'rangeConfig/shared/rules/hideMenuRules';
import {
  hideMixedBestaFramesInSceneErrorMessage,
  hideNoBestaFramesInSceneErrorMessage
} from '../rules/hideMenuRules';

const framesSupportsSupportLeg = (coreApi: CoreApi) => {
  const floorstandingFrames = coreApi.entities
    .getByComponent(ComponentIds.FRAME_COMPONENT_ID)
    .filter(frame => coreApi.entities.isEntityPlacedOnFloor(frame));

  const widths = floorstandingFrames.map(
    frame =>
      frame.getComponent<CommunicationSizeComponent>(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)
        ?.size.width
  );
  if (widths.includes(1200) || widths.includes(1800)) {
    return true;
  }
  return false;
};

const autoFillLegs = ({ coreApi, props, dispatch, rootState }: BehaviorParameters) => {
  const productRef = props.coreProductId;
  const product = rootState.products.inMarket.find(p => p.coreProductId === productRef);
  const supportLegChoiceForgotten = rootState.sheets.supportLegChoice.forgetChoice;
  const selectedEntity = coreApi.entities.findSelectedEntity();
  const frameForSelectedLeg =
    selectedEntity?.hasComponent(ComponentIds.LEG_COMPONENT_ID) &&
    HierarchyUtils.findInUpwardsHierarchy(selectedEntity, (entity: Entity) =>
      entity.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
    );

  const scene = coreApi.entities.getScene();
  let useSupportLeg = scene?.getComponent<FrameSelectedLegComponent>(FrameSelectedLegComponent.id)
    ?.useSupportLeg;
  useSupportLeg = useSupportLeg === undefined ? true : useSupportLeg;

  const isNannarpLegs = product?.templateId === 'leg-nannarp';
  const isDefaultLegs = product?.type === 'default-leg';

  const supports = framesSupportsSupportLeg(coreApi);

  if (!product) return;

  let placementResults: (PlacementResult | undefined)[] | undefined = undefined;
  // CASE A: include support leg automatically
  if (useSupportLeg || isNannarpLegs) {
    placementResults = coreApi
      .getSystem<BestaFrameLegsFeatureSystem>('BestaFrameLegsFeatureSystem')
      ?.setSelectedLeg(productRef, true);
  } else {
    // CASE B: ask if user want support leg if it is possible

    if (supports && supportLegChoiceForgotten && !isDefaultLegs) {
      dispatch(
        setQuestionSheetVisibleWithProps({
          modalVisible: true,
          title: 'storageone.supportLegsSheetTitle',
          preamble: 'storageone.supportLegsSheetBody',
          primaryButtonText: 'storageone.supportLegsSheetPrimaryAction',
          secondaryButtonText: 'storageone.supportLegsSheetSecondaryAction',
          onAccept: () => {
            placementResults = coreApi
              .getSystem<BestaFrameLegsFeatureSystem>('BestaFrameLegsFeatureSystem')
              ?.setSelectedLeg(productRef, true);
            dispatch(setQuestionSheetVisible(false));
          },
          onDismiss: () => {
            placementResults = coreApi
              .getSystem<BestaFrameLegsFeatureSystem>('BestaFrameLegsFeatureSystem')
              ?.setSelectedLeg(productRef, false);
            dispatch(setQuestionSheetVisible(false));
          },
          imageRealtivePath: supportLegsImage
        } as QuestionSheetState)
      );
      dispatch(setSupportLegChoice({ forgetChoice: false, productRef }));
    } else {
      // CASE C: use regular legs
      placementResults = coreApi
        .getSystem<BestaFrameLegsFeatureSystem>('BestaFrameLegsFeatureSystem')
        ?.setSelectedLeg(productRef, false);
    }
  }

  const determinePlacementAttemptErrorMessage = () => {
    const legsCollision = placementResults
      ?.map(p => p?.errors)
      .flat()
      .find(e => (e as any)?.collisions?.length > 0);

    if (!hideNoFramesInSceneErrorMessage(coreApi)) {
      return ErrorMessages.NoFramesInScene;
    } else if (!hideNoBestaFramesInSceneErrorMessage(coreApi)) {
      return ErrorMessages.AddingBestaToEketFrames;
    } else if (legsCollision) {
      return ErrorMessages.LegsWontFit;
    } else if (!hideMixedBestaFramesInSceneErrorMessage(coreApi)) {
      return ErrorMessages.AddingToMixedBestaEketFrames;
    }
    return null;
  };
  const placementAttemptErrorMessage = determinePlacementAttemptErrorMessage();
  if (placementAttemptErrorMessage) {
    coreApi.emit.onPlacementAttempt(placementAttemptErrorMessage);
  }

  if (frameForSelectedLeg) {
    const leg = HierarchyUtils.descendants(frameForSelectedLeg).find(entity =>
      entity.hasComponent(ComponentIds.LEG_COMPONENT_ID)
    );
    leg && coreApi.entities.select(leg);
  }
};

export default autoFillLegs;
